import { useEffect, useState } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { AddressBookService } from '@services/AddressBookService';
import { loadDictionaryItems, peekDictionaryItems } from '@services/dictionaryCache';
import { IAbookTab } from '@models/Forms/IForms';
import { IAddressBookData } from '@models/addressbook/IAddressBookData';
import { getLoadOptionsQuery } from '@utils/loadOptionsConverter';
import { isPersonTab } from '@utils/abookTabVisibility';
import { PICKER_SEARCH_DELAY, PICKER_TAKE } from '@atoms/PickerCombobox/pickerLimits';

interface ILoadResult {
    rows: IAddressBookData[];
    total: number;
    recent: IAddressBookData[];
}

export interface IAbookComboboxItems {
    status: 'loading' | 'ready' | 'error';
    /** Строки ещё не под текущий поиск (debounce или запрос в пути): выбирать из них по Enter нельзя. */
    pending: boolean;
    rows: IAddressBookData[];
    recent: IAddressBookData[];
    /** Сколько всего строк подходит: может быть больше показанных. */
    total: number;
    retry: () => void;
}

/** Сотрудник ищется по ФИО, подразделению и должности, группа — по наименованию. */
const searchFilter = (tab: IAbookTab, query: string) =>
    isPersonTab(tab)
        ? [
              ['fullName', 'contains', query],
              'or',
              ['department', 'contains', query],
              'or',
              ['attributes.Post', 'contains', query],
          ]
        : ['fullName', 'contains', query];

const page = async (tab: IAbookTab, formValues: string, filter?: unknown) => {
    const options = getLoadOptionsQuery({
        skip: 0,
        take: PICKER_TAKE,
        requireTotalCount: true,
        filter,
        sort: [{ selector: 'name', desc: false }],
    });
    const response = await AddressBookService.fetchListPage(tab.cache, options, formValues);
    return { rows: response.data.data ?? [], total: response.data.totalCount ?? 0 };
};

/** Строки вкладки по ключам — только проходящие её условия (недавние, ВРИО, недостающие строки значения). */
export const fetchTabItemsByKeys = async (tab: IAbookTab, formValues: string, keys: string[]) => {
    const options = getLoadOptionsQuery({ skip: 0, take: keys.length });
    const response = await AddressBookService.fetchListPage(tab.cache, options, formValues, keys);
    return response.data.data ?? [];
};

async function fetchItems(tab: IAbookTab, formValues: string, query: string, recentKeys: string[]) {
    const [main, recent] = await Promise.all([
        page(tab, formValues, query ? searchFilter(tab, query) : undefined),
        recentKeys.length ? fetchTabItemsByKeys(tab, formValues, recentKeys) : [],
    ]);
    return {
        rows: main.rows,
        total: main.total,
        recent: recentKeys.flatMap((key) => recent.find((row) => String(row.key) === key) ?? []),
    };
}

/**
 * Строки выпадающего списка адресной книги для вкладки.
 *
 * Выборку и поиск делает сервер по условиям вкладки и значениям формы (`formValues`, вычисляются при открытии);
 * недавние запрашиваются ключами той же вкладки — чужие в список не попадут. Прошлый ответ по тому же ключу
 * показывается сразу (кэш выбора из справочников), свежий заменяет его по приходу.
 */
export function useAbookComboboxItems(
    tab: IAbookTab,
    formValues: string,
    query: string,
    recentKeys: string[],
): IAbookComboboxItems {
    // строки другой вкладки не показываются под этой, пока не придут свои
    const [result, setResult] = useState<ILoadResult & { scope: string }>();
    const [status, setStatus] = useState<IAbookComboboxItems['status']>('loading');
    const [attempt, setAttempt] = useState(0);
    const trimmed = query.trim();
    const [debounced] = useDebouncedValue(trimmed, PICKER_SEARCH_DELAY);
    const serverQuery = trimmed === '' ? '' : debounced;
    const scope = `addressbook\n${tab.cache}`;
    const own = result?.scope === scope ? result : undefined;

    useEffect(() => {
        let cancelled = false;
        const recent = serverQuery ? [] : recentKeys;
        const key = JSON.stringify([formValues, serverQuery, recent]);
        const cached = peekDictionaryItems<ILoadResult>(scope, key);
        if (cached) {
            setResult({ ...cached, scope });
            setStatus('ready');
        } else {
            setStatus('loading');
        }
        loadDictionaryItems(scope, key, () => fetchItems(tab, formValues, serverQuery, recent)).then(
            (fresh) => {
                if (cancelled) return;
                setResult({ ...fresh, scope });
                setStatus('ready');
            },
            () => !cancelled && !cached && setStatus('error'),
        );
        return () => {
            cancelled = true;
        };
    }, [tab, scope, formValues, serverQuery, recentKeys, attempt]);

    return {
        status,
        pending: status !== 'ready' || serverQuery !== trimmed,
        rows: own?.rows ?? [],
        recent: serverQuery ? [] : (own?.recent ?? []),
        total: own?.total ?? 0,
        retry: () => setAttempt((n) => n + 1),
    };
}
